import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import { ChevronDown, ChevronRight, DollarSign, Eraser } from 'lucide-react';

// Campaign-scoped maintenance actions: retroactive cost update ("Update
// costs"), destructive statistics reset, and the alternative tracking-link
// prefixes. Kept as a self-contained component so the large CampaignDetail
// only mounts <CampaignMaintenance campaignId={id} />.
export default function CampaignMaintenance({ campaignId }) {
  const { t } = useT();
  // Rarely needed: collapsed until the header is clicked.
  const [open, setOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [cleanOpen, setCleanOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aliases, setAliases] = useState(null);
  const [form, setForm] = useState({
    start: '',
    end: '',
    cost: '',
    currency: 'USD',
    mode: 'total',
    only_unique: false,
    skip_bots: true,
  });

  useEffect(() => {
    if (!campaignId) return;
    axios
      .get(API + '/campaigns/' + campaignId + '/url-aliases')
      .then((res) => setAliases(res.data || null))
      .catch(() => setAliases(null));
  }, [campaignId]);

  const submitCosts = async () => {
    if (!form.start) {
      toast.error(t('Start date is required'));
      return;
    }
    const cost = Number(form.cost);
    if (form.cost === '' || Number.isNaN(cost) || cost < 0) {
      toast.error(t('Enter a valid cost'));
      return;
    }
    try {
      setSaving(true);
      const res = await axios.post(API + '/campaigns/' + campaignId + '/update-costs', {
        start: form.start,
        end: form.end || null,
        cost,
        currency: (form.currency || 'USD').toUpperCase(),
        mode: form.mode,
        only_unique: form.only_unique,
        skip_bots: form.skip_bots,
      });
      const n = res.data?.updated ?? 0;
      setCostOpen(false);
      toast.success(t('Updated {n} clicks').replace('{n}', n));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('Failed to update costs'));
    } finally {
      setSaving(false);
    }
  };

  const cleanStats = async () => {
    try {
      await axios.post(API + '/campaigns/' + campaignId + '/clean-stats', { confirm: true });
      setCleanOpen(false);
      toast.success(t('Statistics deleted'));
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t('Failed to delete statistics'));
    }
  };

  return (
    <Card>
      <CardHeader
        role="button"
        tabIndex={0}
        aria-expanded={open}
        className="cursor-pointer select-none"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        data-testid="maintenance-toggle"
      >
        <CardTitle className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {t('Maintenance & costs')}
        </CardTitle>
        <CardDescription>
          {t('Retroactively rewrite click costs, reset statistics, or grab an alternative tracking link.')}
        </CardDescription>
      </CardHeader>
      {open && (
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          <Dialog open={costOpen} onOpenChange={setCostOpen}>
            <DialogTrigger asChild>
              <Button variant="outline"><DollarSign className="w-4 h-4 mr-2" />{t('Update costs')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('Update costs')}</DialogTitle>
                <DialogDescription>
                  {t('Retroactively (re)write per-click cost for clicks in a time range. Total mode spreads the amount across matched clicks; per-click applies it to each.')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Start date')}</Label>
                    <Input type="date" value={form.start} onChange={(e) => setForm((p) => ({ ...p, start: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('End date')}</Label>
                    <Input type="date" value={form.end} onChange={(e) => setForm((p) => ({ ...p, end: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Cost')}</Label>
                    <Input type="number" step="0.0001" value={form.cost} onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))} placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Currency')}</Label>
                    <Input value={form.currency} maxLength={3} onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('Mode')}</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm((p) => ({ ...p, mode: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="total">{t('Total (spread across clicks)')}</SelectItem>
                      <SelectItem value="per_click">{t('Per click')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.only_unique} onChange={(e) => setForm((p) => ({ ...p, only_unique: e.target.checked }))} />
                  {t('Only unique clicks')}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.skip_bots} onChange={(e) => setForm((p) => ({ ...p, skip_bots: e.target.checked }))} />
                  {t('Skip bot clicks')}
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCostOpen(false)}>{t('Cancel')}</Button>
                <Button onClick={submitCosts} disabled={saving}>{saving ? t('Saving…') : t('Update')}</Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => setCleanOpen(true)}>
            <Eraser className="w-4 h-4 mr-2" />{t('Clean statistics')}
          </Button>
        </div>

        {aliases && Array.isArray(aliases.aliases) && aliases.aliases.length > 0 ? (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t('Alternative link prefixes')}</Label>
            <p className="text-xs text-muted-foreground">
              {t('Any of these path prefixes route the same tracking id — rotate them across sources to dodge blocklists.')}
            </p>
            <div className="flex flex-wrap gap-1">
              {aliases.aliases.map((a) => (
                <Badge key={a} variant={a === aliases.rotation ? 'default' : 'outline'} className="text-xs font-mono">
                  /{a}/{aliases.tracking_id}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
      )}

      <ConfirmDialog
        open={cleanOpen}
        onOpenChange={setCleanOpen}
        title={t('Delete statistics')}
        description={t('This permanently deletes all clicks and conversions for this campaign. The campaign configuration is kept. This cannot be undone.')}
        confirmLabel={t('Delete statistics')}
        onConfirm={cleanStats}
      />
    </Card>
  );
}
